import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import type { AccommodationCardLegacyProps } from './AccommodationCardLegacy'
import { Button, Icon, tk } from '@sunwebgroup/ui-components'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { styled } from 'styled-components'
import AccommodationCardLegacy from './AccommodationCardLegacy'

interface AccommodationTipsSliderLegacyProps {
  items: AccommodationCardLegacyProps[]
  participants?: RoomsParticipantDistribution
}

const Container = styled.section`
  position: relative;
`

const Slider = styled.div`
  overflow: hidden;
  border-radius: ${tk('radius_large')};
`

const SliderContainer = styled.div`
  display: flex;
`

const Slide = styled.div`
  flex: 0 0 100%;
`

const Dots = styled.div`
  position: absolute;
  bottom: 16px;
  width: 100%;
  display: flex;
  justify-content: center;

  @media ${tk('mq_tablet')} {
    bottom: 20px;
  }
`

const Dot = styled.div<{ $active: boolean }>`
  width: ${tk('spacing_xs')};
  height: ${tk('spacing_xs')};
  border-radius: 50%;
  background-color: ${props => (props.$active ? '#fff' : tk('color_palette_transparent_white_75'))};
  margin: 0 4px;
`

const StyledButton = styled(Button)`
  display: none;

  @media ${tk('mq_tablet')} {
    display: inline-flex;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;

    ${Container}:hover & {
      opacity: 1;
    }
  }
`

const StyledLeftButton = styled(StyledButton)`
  left: 33px;
`

const StyledRightButton = styled(StyledButton)`
  right: 33px;
`
/**
 * @deprecated This file is deprecated and will be removed in the future.
 * TODO: To be deleted when FF ACTIVATE_UDS_SEARCH_PRODUCT_CARD is deleted.
 */
export default function AccommodationTipsLegacySlider({ items, participants }: AccommodationTipsSliderLegacyProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)

  function navigate(direction: 'previous' | 'next') {
    if (direction === 'previous') {
      emblaApi?.scrollPrev()
    }
    else {
      emblaApi?.scrollNext()
    }
  }

  const onSelect = useCallback(() => {
    if (emblaApi !== undefined) {
      const index = emblaApi.selectedScrollSnap()
      setSelectedIndex(index)
    }
  }, [emblaApi])

  useEffect(() => {
    setScrollSnaps(emblaApi?.scrollSnapList() ?? [])
    emblaApi?.on('select', onSelect)

    return () => {
      emblaApi?.off('select', onSelect)
    }
  }, [emblaApi, onSelect])
  return (
    <Container data-qa="accommodation-tips">
      <Slider ref={emblaRef}>
        <SliderContainer>
          {items.map((item, index) => (
            <Slide key={item.id}>
              <AccommodationCardLegacy
                arrivalDate={item.arrivalDate}
                departureDate={item.departureDate}
                departureDateRaw={item.departureDateRaw}
                durationRaw={item.durationRaw}
                url={item.url}
                id={item.id}
                images={item.images}
                locations={item.locations}
                mealPlan={item.mealPlan}
                name={item.name}
                occupancy={item.occupancy}
                price={item.price}
                rating={item.rating}
                slideIndex={index}
                transportType={item.transportType}
                userRating={item.userRating}
                userRatingText={item.userRatingText}
                outboundDepartureAirportName={item.outboundDepartureAirportName}
                participants={participants}
              />
            </Slide>
          ))}
        </SliderContainer>
      </Slider>
      {items.length > 1 && (
        <>
          <StyledLeftButton $hierarchy="secondary" $withoutColor $size="small" onClick={() => navigate('previous')}>
            <Icon glyph="IconChevronLeft" />
          </StyledLeftButton>
          <StyledRightButton $hierarchy="secondary" $withoutColor $size="small" onClick={() => navigate('next')}>
            <Icon glyph="IconChevronRight" />
          </StyledRightButton>
        </>
      )}
      {scrollSnaps.length > 0 && (
        <Dots>
          {scrollSnaps.map((_, index) => (
            <Dot $active={index === selectedIndex} key={index} />
          ))}
        </Dots>
      )}
    </Container>
  )
}
